import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../Toast";

type Props = {
  /** Upit koji nosi ISTI period i istu zemlju koje nosi i ekran. */
  upit: string;
  /** Natpis perioda, da čovek pre slanja vidi šta tačno šalje. */
  period: string;
};

/**
 * Šalje mesečni izveštaj na zadatu adresu — i dokument i radnu svesku.
 *
 * <p>Mejl ide sa KORISNIKOVOG naloga, onog koji je povezao u podešavanjima (Gmail sa app
 * lozinkom), pa poruka stiže sa njegove adrese i ostaje u njegovom „Sent". Ne ide kroz portalskog
 * pošiljaoca obaveštenja: on na razvojnoj instanci gleda u lokalni hvatač pošte i progutao bi
 * poruku bez reči.
 *
 * <p>Fajl nosi nabavnu cenu, maržu po kupcu i stanje potraživanja, pa ekran kaže KOJI period
 * šalje i KOME, pre nego što se pošalje. Adresa koja nije adresa pada ovde, a ne na serveru pošte.
 */
export function SendReportModal({ upit, period }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [otvoren, setOtvoren] = useState(false);
  const [primaoci, setPrimaoci] = useState("");
  const [salje, setSalje] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const polje = useRef<HTMLInputElement>(null);

  // Fokus ulazi u polje čim se prozor otvori: inače čovek klikne dugme pa mora i da klikne polje.
  useEffect(() => {
    if (otvoren) polje.current?.focus();
  }, [otvoren]);

  function zatvori() {
    if (salje) return;
    setOtvoren(false);
    setGreska(null);
  }

  async function posalji() {
    if (salje) return;
    const uneto = primaoci.trim();
    if (!uneto) {
      setGreska(t("report.mail.noRecipient", "Unesite bar jednu adresu."));
      return;
    }
    setSalje(true);
    setGreska(null);
    try {
      const p = new URLSearchParams(upit);
      const res = await fetch("/api/exports/management-report/email", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // `recipients`, ne `to`: period već ima svoj `to`, a raspon ne može da ustupi ime.
          recipients: uneto,
          kind: p.get("kind"),
          year: p.get("year") ? Number(p.get("year")) : null,
          month: p.get("month") ? Number(p.get("month")) : null,
          quarter: p.get("quarter") ? Number(p.get("quarter")) : null,
          asOf: p.get("asOf"),
          from: p.get("from"),
          to: p.get("to"),
          country: p.get("country"),
          sporna: p.get("sporna") === "true",
        }),
      });
      if (!res.ok) {
        // Server ima svoju rečenicu za svaku odbijenicu; ona je korisnija od „greška 400".
        const telo = await res.json().catch(() => null);
        throw new Error(
          (telo && (telo.message || telo.error)) ||
            t("report.mail.failed", "Slanje nije uspelo."),
        );
      }
      toast.success(t("report.mail.sent", "Izveštaj je poslat."));
      setOtvoren(false);
      setPrimaoci("");
    } catch (e) {
      setGreska((e as Error).message);
    } finally {
      setSalje(false);
    }
  }

  return (
    <>
      <button type="button" className="btn" onClick={() => setOtvoren(true)}>
        {t("report.mail.button", "Pošalji na mejl")}
      </button>
      {otvoren && (
        <div role="dialog" aria-modal="true" style={pokrivac} onClick={zatvori}>
          <div style={ploca} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 4px" }}>{t("report.mail.title", "Pošalji izveštaj na mejl")}</h3>
            <div className="sub" style={{ marginBottom: 12 }}>
              {t("report.mail.lead", {
                period,
                defaultValue: "Šalju se dokument (PDF) i radna sveska (Excel) za period {{period}}.",
              })}
            </div>
            <label className="field" style={{ display: "block" }}>
              <span>{t("report.mail.to", "Adresa primaoca")}</span>
              <input
                ref={polje}
                className="input"
                type="email"
                multiple
                value={primaoci}
                placeholder="ime.prezime@bankom.rs"
                disabled={salje}
                onChange={(e) => setPrimaoci(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") void posalji();
                }}
              />
            </label>
            <div className="sub" style={{ marginTop: 6 }}>
              {t("report.mail.hint", "Više adresa razdvojite zarezom, najviše pet.")}
            </div>
            {greska && (
              <div className="tag err" style={{ marginTop: 10, display: "block", padding: "8px 10px" }}>
                {greska}
              </div>
            )}
            <div style={dugmad}>
              <button type="button" className="btn" onClick={zatvori} disabled={salje}>
                {t("action.cancel", "Odustani")}
              </button>
              <button type="button" className="btn primary" onClick={() => void posalji()} disabled={salje}>
                {salje ? t("report.mail.sending", "Šalje se…") : t("report.mail.send", "Pošalji")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const pokrivac: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
};

const ploca: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: 10,
  padding: 20,
  width: 420,
  maxWidth: "calc(100vw - 32px)",
  boxShadow: "var(--shadow-2)",
};

const dugmad: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  marginTop: 16,
};
